import {
	createFileRoute,
	useCanGoBack,
	useNavigate,
	useRouteContext,
	useRouter,
} from "@tanstack/react-router";
import {
	Suspense,
	useCallback,
	useEffect,
	useMemo,
	useRef,
	useState,
} from "react";
import { graphql, useLazyLoadQuery, useMutation } from "react-relay";
import { toast } from "sonner";
import { Icon } from "@/components/icon";
import { Button } from "@/components/ui/button";
import {
	Collapsible,
	CollapsibleContent,
	CollapsibleTrigger,
} from "@/components/ui/collapsible";
import { Input } from "@/components/ui/input";
import {
	Select,
	SelectContent,
	SelectItem,
	SelectTrigger,
	SelectValue,
} from "@/components/ui/select";
import { Separator } from "@/components/ui/separator";
import { Skeleton } from "@/components/ui/skeleton";
import { Spinner } from "@/components/ui/spinner";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { cn } from "@/utils";
import type { temaQuery } from "./__generated__/temaQuery.graphql";
import type { temaUpdateMutation } from "./__generated__/temaUpdateMutation.graphql";

export const Route = createFileRoute("/_logged/c/$club/(standalone)/tema")({
	component: () => (
		<Suspense fallback={<ThemeSkeleton />}>
			<ThemeEditor />
		</Suspense>
	),
});

function ThemeSkeleton() {
	return (
		<div className="flex flex-col h-screen bg-background">
			<header className="flex items-center justify-between h-14 px-4 border-b">
				<div className="flex items-center gap-2">
					<Skeleton className="h-4 w-32" />
					<span className="text-muted-foreground">/</span>
					<Skeleton className="h-4 w-20" />
				</div>
				<div className="flex items-center gap-2">
					<Skeleton className="h-8 w-20" />
					<Skeleton className="h-8 w-20" />
				</div>
			</header>
			<div className="flex-1 flex">
				<div className="w-80 border-r p-4 space-y-4">
					<Skeleton className="h-10 w-full" />
					<Skeleton className="h-40 w-full" />
					<Skeleton className="h-40 w-full" />
				</div>
				<div className="flex-1 p-6">
					<Skeleton className="h-full w-full rounded-lg" />
				</div>
			</div>
		</div>
	);
}

interface ThemeStyles {
	primary: string;
	"primary-foreground": string;
	secondary: string;
	"secondary-foreground": string;
	accent: string;
	"accent-foreground": string;
	background: string;
	foreground: string;
	muted: string;
	"muted-foreground": string;
	card: string;
	"card-foreground": string;
	border: string;
	input: string;
	ring: string;
	"font-sans": string;
	radius?: string;
	"shadow-color"?: string;
	"shadow-blur"?: string;
	"shadow-spread"?: string;
	"shadow-opacity"?: string;
	"shadow-offset-x"?: string;
	"shadow-offset-y"?: string;
	[key: string]: string | undefined;
}

const FONT_OPTIONS = [
	{ value: "Geist", label: "Clean", family: "Geist" },
	{ value: "Space Grotesk", label: "Creative", family: "Space Grotesk" },
	{ value: "Libre Baskerville", label: "Luxury", family: "Libre Baskerville" },
	{ value: "Geist Mono", label: "Technical", family: "Geist Mono" },
	{ value: "Roboto Slab", label: "Slab", family: "Roboto Slab" },
	{ value: "Comic Sans MS", label: "Cartoon", family: "Comic Sans MS" },
] as const;

function getGoogleFontUrl(fontFamily: string): string {
	const fontName = fontFamily.replace(/\s+/g, "+");
	return `https://fonts.googleapis.com/css2?family=${fontName}:wght@400;500;600;700&display=swap`;
}

interface ColorFieldProps {
	label: string;
	value: string;
	onChange: (value: string) => void;
	disabled?: boolean;
}

function ColorField({ label, value, onChange, disabled }: ColorFieldProps) {
	return (
		<div className="flex flex-col gap-1.5">
			<span className="text-xs text-muted-foreground">{label}</span>
			<div className="flex items-center gap-2">
				<div className="relative">
					<input
						type="color"
						value={value}
						onChange={(e) => onChange(e.target.value)}
						disabled={disabled}
						className="h-9 w-9 cursor-pointer overflow-hidden rounded border border-input disabled:opacity-50 disabled:cursor-not-allowed"
					/>
				</div>
				<Input
					type="text"
					value={value}
					onChange={(e) => onChange(e.target.value)}
					disabled={disabled}
					className="h-9 flex-1 font-mono text-sm uppercase"
					placeholder="#000000"
				/>
			</div>
		</div>
	);
}

interface SliderFieldProps {
	label: string;
	value: string | undefined;
	onChange: (value: string) => void;
	unit?: string;
	min?: number;
	max?: number;
	step?: number;
	defaultValue?: number;
}

function SliderField({
	label,
	value,
	onChange,
	unit = "",
	min = 0,
	max = 100,
	step = 1,
	defaultValue = 0,
}: SliderFieldProps) {
	const numValue = value ? parseFloat(value) : defaultValue;

	return (
		<div className="flex flex-col gap-2">
			<div className="flex items-center justify-between">
				<span className="text-xs text-muted-foreground">{label}</span>
				<span className="text-xs font-mono text-muted-foreground">
					{numValue}
					{unit}
				</span>
			</div>
			<input
				type="range"
				value={numValue}
				onChange={(e) => onChange(e.target.value)}
				min={min}
				max={max}
				step={step}
				className="w-full h-2 bg-muted rounded-full appearance-none cursor-pointer [&::-webkit-slider-thumb]:appearance-none [&::-webkit-slider-thumb]:w-4 [&::-webkit-slider-thumb]:h-4 [&::-webkit-slider-thumb]:rounded-full [&::-webkit-slider-thumb]:bg-primary [&::-webkit-slider-thumb]:cursor-pointer [&::-webkit-slider-thumb]:border-2 [&::-webkit-slider-thumb]:border-background [&::-webkit-slider-thumb]:shadow-sm [&::-moz-range-thumb]:w-4 [&::-moz-range-thumb]:h-4 [&::-moz-range-thumb]:rounded-full [&::-moz-range-thumb]:bg-primary [&::-moz-range-thumb]:cursor-pointer [&::-moz-range-thumb]:border-2 [&::-moz-range-thumb]:border-background"
			/>
		</div>
	);
}

interface ColorSectionProps {
	title: string;
	defaultOpen?: boolean;
	children: React.ReactNode;
}

function ColorSection({
	title,
	defaultOpen = false,
	children,
}: ColorSectionProps) {
	return (
		<Collapsible defaultOpen={defaultOpen}>
			<CollapsibleTrigger className="flex w-full items-center justify-between py-3 text-sm font-medium hover:text-foreground transition-colors group">
				{title}
				<Icon
					name="ChevronDown"
					className="size-4 text-muted-foreground transition-transform group-data-[state=open]:rotate-180"
				/>
			</CollapsibleTrigger>
			<CollapsibleContent className="pb-4">
				<div className="flex flex-col gap-4">{children}</div>
			</CollapsibleContent>
		</Collapsible>
	);
}

function ThemeEditor() {
	const { club } = useRouteContext({ from: "/_logged/c/$club" });
	const navigate = useNavigate({ from: Route.fullPath });
	const router = useRouter();
	const canGoBack = useCanGoBack();

	const data = useLazyLoadQuery<temaQuery>(
		graphql`
			query temaQuery($slug: String!) {
				club(slug: $slug) {
					id
					name
					themes {
						variant
						styles
					}
				}
				defaultThemes {
					id
					name
					variant
					styles
					tier
					rawID
				}
			}
		`,
		{ slug: club.slug },
	);

	const [updateTheme, isUpdating] = useMutation<temaUpdateMutation>(graphql`
		mutation temaUpdateMutation($club: String!, $payload: UpdateClubThemeInput!) {
			clubThemeUpdate(club: $club, payload: $payload) {
				variant
				styles
			}
		}
	`);

	const currentTheme = data.club.themes?.[0];
	const [selectedVariant, setSelectedVariant] = useState<"LIGHT" | "DARK">(
		currentTheme?.variant === "DARK" ? "DARK" : "LIGHT",
	);

	const filteredThemes = useMemo(
		() => data.defaultThemes.filter((t) => t.variant === selectedVariant),
		[data.defaultThemes, selectedVariant],
	);

	const initialStyles: ThemeStyles = currentTheme?.styles ||
		filteredThemes[0]?.styles || {
			primary: "#22c55e",
			"primary-foreground": "#ffffff",
			secondary: "#f3f4f6",
			"secondary-foreground": "#1f2937",
			accent: "#dcfce7",
			"accent-foreground": "#166534",
			background: "#fcfcfc",
			foreground: "#171717",
			muted: "#f5f5f5",
			"muted-foreground": "#737373",
			card: "#ffffff",
			"card-foreground": "#171717",
			border: "#e5e5e5",
			input: "#e5e5e5",
			ring: "#22c55e",
			"font-sans": "Inter",
		};

	const [styles, setStyles] = useState<ThemeStyles>(initialStyles);
	const [selectedThemeName, setSelectedThemeName] = useState<string>(
		filteredThemes.find((t) => t.styles.primary === initialStyles.primary)
			?.name ||
			filteredThemes[0]?.name ||
			"Forest",
	);
	const [activeTab, setActiveTab] = useState("cores");
	const [hasChanges, setHasChanges] = useState(false);

	const iframeRef = useRef<HTMLIFrameElement>(null);

	const previewTheme = useCallback(() => {
		if (iframeRef.current) {
			const iframe = iframeRef.current;

			const fonts = [styles["font-sans"]].filter(Boolean);
			const fontUrls = fonts.map((f) => getGoogleFontUrl(f));

			fontUrls.forEach((href) => {
				const existing = document.querySelector(`link[href="${href}"]`);
				if (!existing) {
					const link = document.createElement("link");
					link.rel = "stylesheet";
					link.href = href;
					document.head.appendChild(link);
				}
			});

			iframe.contentWindow?.postMessage(
				{
					type: "TWEAKCN_THEME_UPDATE",
					payload: {
						themeState: {
							styles: {
								light: selectedVariant === "LIGHT" ? styles : {},
								dark: selectedVariant === "DARK" ? styles : {},
							},
							currentMode: selectedVariant.toLowerCase(),
							hslAdjustments: {
								hueShift: 0,
								saturationScale: 1,
								lightnessScale: 1,
							},
						},
					},
				},
				"*",
			);
		}
	}, [styles, selectedVariant]);

	useEffect(() => {
		previewTheme();
	}, [previewTheme]);

	const updateStyle = (key: string, value: string) => {
		setStyles((prev) => ({ ...prev, [key]: value }));
		setHasChanges(true);
	};

	const handleThemeSelect = (themeName: string) => {
		const theme = filteredThemes.find((t) => t.name === themeName);

		if (theme) {
			setStyles(theme.styles);
			setSelectedThemeName(themeName);
			setHasChanges(true);
		}
	};

	const handleVariantChange = (variant: "LIGHT" | "DARK") => {
		setSelectedVariant(variant);
		// Find a theme with the same name in the new variant, or use the first one
		const themesForVariant = data.defaultThemes.filter(
			(t) => t.variant === variant,
		);
		const sameNameTheme = themesForVariant.find(
			(t) => t.name === selectedThemeName,
		);
		const themeToUse = sameNameTheme || themesForVariant[0];

		if (themeToUse) {
			setStyles(themeToUse.styles);
			setSelectedThemeName(themeToUse.name);
		}
		setHasChanges(true);
	};

	const handleSave = () => {
		updateTheme({
			variables: {
				club: club.slug,
				payload: {
					variants: [
						{
							variant: selectedVariant,
							styles,
						},
					],
				},
			},
			onCompleted: (_response, errors) => {
				if (errors?.length) {
					toast.error(errors[0].message);
					return;
				}
				toast.success("Tema atualizado com sucesso!");
				setHasChanges(false);
			},
			onError: (error) => {
				console.error(error);
				toast.error("Erro ao atualizar tema. Tente novamente.");
			},
		});
	};

	const handleBack = () => {
		if (canGoBack) {
			router.history.back();
		} else {
			navigate({ to: "/c/$club/area-de-membros/personalizacao" });
		}
	};

	return (
		<div className="flex flex-col h-screen bg-background">
			{/* Header */}
			<div className="flex items-center justify-between px-4 py-3 border-b shrink-0">
				<button
					type="button"
					onClick={handleBack}
					className="flex items-center gap-2 text-sm text-muted-foreground hover:text-foreground transition-colors"
				>
					<Icon name="ArrowLeft" className="size-4" />
					Edição de Design
				</button>

				<Button onClick={handleSave} disabled={isUpdating || !hasChanges}>
					{isUpdating && <Spinner className="size-4" />}
					Salvar Alterações
				</Button>
			</div>

			<div className="flex flex-1 overflow-hidden">
				{/* Left Sidebar */}
				<div className="w-80 border-r flex flex-col overflow-hidden shrink-0">
					<div className="p-4 flex flex-col gap-6 overflow-y-auto flex-1">
						{/* Appearance Mode */}
						<div>
							<span className="text-xs font-medium text-muted-foreground uppercase tracking-wider">
								Aparência do Ambiente
							</span>
							<div className="mt-2 flex gap-2">
								<button
									type="button"
									onClick={() => handleVariantChange("LIGHT")}
									className={cn(
										"flex-1 px-4 py-2 rounded-md text-sm font-medium transition-colors flex items-center justify-center gap-2",
										selectedVariant === "LIGHT"
											? "bg-primary text-primary-foreground"
											: "bg-muted text-muted-foreground hover:bg-muted/80",
									)}
								>
									<Icon name="Sun" className="size-4" />
									Light
								</button>
								<button
									type="button"
									onClick={() => handleVariantChange("DARK")}
									className={cn(
										"flex-1 px-4 py-2 rounded-md text-sm font-medium transition-colors flex items-center justify-center gap-2",
										selectedVariant === "DARK"
											? "bg-primary text-primary-foreground"
											: "bg-muted text-muted-foreground hover:bg-muted/80",
									)}
								>
									<Icon name="Moon" className="size-4" />
									Dark
								</button>
							</div>
							<p className="mt-2 text-xs text-muted-foreground">
								Define o modo de visualização dos seus alunos.
							</p>
						</div>

						<Separator />

						{/* Theme Selector */}
						<div>
							<span className="text-xs font-medium text-muted-foreground uppercase tracking-wider">
								Temas
							</span>
							<div className="mt-2">
								<Select
									value={selectedThemeName}
									onValueChange={handleThemeSelect}
								>
									<SelectTrigger className="w-full">
										<SelectValue placeholder="Selecione um tema">
											<div className="flex items-center gap-2">
												<div className="flex items-center gap-0.5">
													{[
														styles.primary,
														styles.accent,
														styles.secondary,
													].map((color, idx) => (
														<div
															key={idx}
															className="size-3 rounded-full border border-border"
															style={{ backgroundColor: color }}
														/>
													))}
												</div>
												{selectedThemeName}
											</div>
										</SelectValue>
									</SelectTrigger>
									<SelectContent>
										{filteredThemes.map((theme) => (
											<SelectItem key={theme.id} value={theme.name}>
												<div className="flex items-center gap-2">
													<div className="flex items-center gap-0.5">
														{[
															theme.styles.primary,
															theme.styles.accent,
															theme.styles.secondary,
														].map((color, idx) => (
															<div
																key={idx}
																className="size-3 rounded-full border border-border"
																style={{ backgroundColor: color }}
															/>
														))}
													</div>
													{theme.name}
												</div>
											</SelectItem>
										))}
									</SelectContent>
								</Select>
							</div>
						</div>

						<Separator />

						{/* Tabs */}
						<Tabs value={activeTab} onValueChange={setActiveTab}>
							<TabsList className="w-full">
								<TabsTrigger value="cores" className="flex-1 border-none">
									Cores
								</TabsTrigger>
								<TabsTrigger value="tipografia" className="flex-1 border-none">
									Tipografia
								</TabsTrigger>
								<TabsTrigger value="outros" className="flex-1 border-none">
									Outros
								</TabsTrigger>
							</TabsList>

							<TabsContent value="cores" className="mt-4 flex flex-col gap-2">
								<ColorSection title="Cores Base" defaultOpen>
									<ColorField
										label="Background"
										value={styles.background}
										onChange={(v) => updateStyle("background", v)}
									/>
									<ColorField
										label="Texto"
										value={styles.foreground}
										onChange={(v) => updateStyle("foreground", v)}
									/>
									<ColorField
										label="Texto Secundário"
										value={styles["muted-foreground"]}
										onChange={(v) => updateStyle("muted-foreground", v)}
									/>
								</ColorSection>

								<Separator />

								<ColorSection title="Cor Primária">
									<ColorField
										label="Primária"
										value={styles.primary}
										onChange={(v) => {
											updateStyle("primary", v);
											updateStyle("ring", v);
										}}
									/>
									<ColorField
										label="Primária - Texto"
										value={styles["primary-foreground"]}
										onChange={(v) => updateStyle("primary-foreground", v)}
									/>
								</ColorSection>

								<Separator />

								<ColorSection title="Cor de Destaque">
									<ColorField
										label="Destaque"
										value={styles.accent}
										onChange={(v) => updateStyle("accent", v)}
									/>
									<ColorField
										label="Destaque - Texto"
										value={styles["accent-foreground"]}
										onChange={(v) => updateStyle("accent-foreground", v)}
									/>
								</ColorSection>

								<Separator />

								<ColorSection title="Cor Secundária">
									<ColorField
										label="Secundária"
										value={styles.secondary}
										onChange={(v) => updateStyle("secondary", v)}
									/>
									<ColorField
										label="Secundária - Texto"
										value={styles["secondary-foreground"]}
										onChange={(v) => updateStyle("secondary-foreground", v)}
									/>
								</ColorSection>

								<Separator />

								<ColorSection title="Card">
									<ColorField
										label="Card"
										value={styles.card}
										onChange={(v) => updateStyle("card", v)}
									/>
									<ColorField
										label="Card - Texto"
										value={styles["card-foreground"]}
										onChange={(v) => updateStyle("card-foreground", v)}
									/>
								</ColorSection>

								<Separator />

								<ColorSection title="Outros">
									<ColorField
										label="Borda"
										value={styles.border}
										onChange={(v) => {
											updateStyle("border", v);
											updateStyle("input", v);
										}}
									/>
									<ColorField
										label="Muted"
										value={styles.muted}
										onChange={(v) => updateStyle("muted", v)}
									/>
								</ColorSection>
							</TabsContent>

							<TabsContent value="tipografia" className="mt-4">
								<div className="flex flex-col gap-4">
									<div className="flex flex-col gap-1.5">
										<span className="text-xs text-muted-foreground">
											Fonte Principal
										</span>
										<Select
											value={styles["font-sans"]}
											onValueChange={(v) => updateStyle("font-sans", v)}
										>
											<SelectTrigger className="w-full">
												<SelectValue>
													<span style={{ fontFamily: styles["font-sans"] }}>
														{FONT_OPTIONS.find(
															(f) => f.value === styles["font-sans"],
														)?.label || styles["font-sans"]}
													</span>
												</SelectValue>
											</SelectTrigger>
											<SelectContent>
												{FONT_OPTIONS.map((font) => (
													<SelectItem key={font.value} value={font.value}>
														<span style={{ fontFamily: font.family }}>
															{font.label}
														</span>
													</SelectItem>
												))}
											</SelectContent>
										</Select>
									</div>
								</div>
							</TabsContent>

							<TabsContent value="outros" className="mt-4 flex flex-col gap-2">
								<ColorSection title="Borda" defaultOpen>
									<ColorField
										label="Cor da Borda"
										value={styles.border}
										onChange={(v) => {
											updateStyle("border", v);
											updateStyle("input", v);
										}}
									/>
									<SliderField
										label="Raio da Borda"
										value={styles.radius}
										onChange={(v) => updateStyle("radius", `${v}px`)}
										unit="px"
										min={0}
										max={24}
										defaultValue={8}
									/>
								</ColorSection>

								<Separator />

								<ColorSection title="Sombra">
									<ColorField
										label="Cor da Sombra"
										value={styles["shadow-color"] || "#000000"}
										onChange={(v) => updateStyle("shadow-color", v)}
									/>
									<SliderField
										label="Desfoque (Blur)"
										value={styles["shadow-blur"]}
										onChange={(v) => updateStyle("shadow-blur", `${v}px`)}
										unit="px"
										min={0}
										max={100}
										defaultValue={0}
									/>
									<SliderField
										label="Espalhamento (Spread)"
										value={styles["shadow-spread"]}
										onChange={(v) => updateStyle("shadow-spread", `${v}px`)}
										unit="px"
										min={-50}
										max={50}
										defaultValue={0}
									/>
									<SliderField
										label="Opacidade"
										value={styles["shadow-opacity"]}
										onChange={(v) => updateStyle("shadow-opacity", `${v}%`)}
										unit="%"
										min={0}
										max={100}
										defaultValue={10}
									/>
									<div className="grid grid-cols-2 gap-4">
										<SliderField
											label="Offset X"
											value={styles["shadow-offset-x"]}
											onChange={(v) => updateStyle("shadow-offset-x", `${v}px`)}
											unit="px"
											min={-50}
											max={50}
											defaultValue={0}
										/>
										<SliderField
											label="Offset Y"
											value={styles["shadow-offset-y"]}
											onChange={(v) => updateStyle("shadow-offset-y", `${v}px`)}
											unit="px"
											min={-50}
											max={50}
											defaultValue={0}
										/>
									</div>
								</ColorSection>
							</TabsContent>
						</Tabs>
					</div>
				</div>

				{/* Preview Area */}
				<div className="flex-1 p-6 bg-muted/30 overflow-hidden flex flex-col">
					<div className="mb-4 shrink-0">
						<h3 className="font-medium">Pré-Visualização</h3>
						<p className="text-sm text-muted-foreground">
							Veja em tempo real como a sua área de membros está ficando
						</p>
					</div>
					<div className="flex-1 rounded-lg border shadow-sm overflow-hidden bg-background">
						<iframe
							ref={iframeRef}
							src="https://comunidade.scarf.club/comunidade?preview=true"
							className="w-full h-full border-0"
							title="Preview"
							onLoad={previewTheme}
						/>
					</div>
				</div>
			</div>
		</div>
	);
}
